import {
  MeidenConnectCredentialsProvider,
  useMeidenConnectIdpTableGetUser,
  useMeidenConnectUser,
} from "@meiden-connect/auth-frontend-sdk";
import {
  broadcastTokenMessage,
  hasMeidenConnectAuthorizationCallbackParameters,
  hasPendingMeidenConnectTokenStoreReset,
  getTokenEndpointRateLimitedUntil,
  MeidenConnectAuthLoadingPane,
  MeidenConnectAuthorizationCallbackPane,
  MeidenConnectAuthRecoverySuccessMarker,
  MeidenConnectTokenStoreResetPane,
  setTokenEndpointRateLimitedUntil as rememberTokenEndpointRateLimitedUntil,
  subscribeTokenBroadcast,
} from "@osakabe/auth-service-provider";
import {
  Component,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ErrorInfo,
  type ReactNode,
} from "react";
import { AuthenticatedApiRequestProvider, MockApiRequestProvider } from "./api-request-context";
import { AuthSessionContext, type AuthSessionContextValue } from "./auth-session-context";
import { EmbedAuthShell } from "./embed-auth-shell";
import { MockCurrentActorProvider } from "./use-current-actor";
import { isEmbedMode } from "../lib/embed-mode";
import {
  purgeAllMeidenConnectTokens,
  readMeidenConnectIdToken,
  restoreSessionSnapshotFromMeidenConnect,
} from "./session-bootstrap";
import {
  buildMeidenConnectSignOutUrl,
  clearSignedOutReturnMarker,
  hasSignedOutReturnMarker,
  navigateToSignOut,
  releaseSignOutAttempt,
  tryBeginSignOut,
} from "./sign-out";
import {
  clearSessionSnapshot,
  clearSignedOut,
  getSignedOutState,
  isSignedOut,
  markSignOutCompleted,
  markSignOutFailed,
  loadSessionSnapshot,
  markSignedOut,
  saveSessionSnapshot,
  subscribeSessionSnapshot,
} from "./storage";
import type { SessionSnapshot } from "./types";
import { getWebConfig } from "../env";

interface Props {
  readonly children: ReactNode;
}

const MOCK_SESSION: SessionSnapshot = {
  userId: "mock-canvas-user",
  name: "Mock 利用者",
  source: "mock",
};

// MEIDEN CONNECT 認証 Shell。
// - authMode === "mock" のときは固定 SessionSnapshot で AuthSessionContext を提供する
// - authMode === "required" のときは以下の構成で動作する:
//   1. 起動時に localStorage の SessionSnapshot キャッシュ、続いて IndexedDB の MEIDEN CONNECT
//      トークンから復元を試み、復元できた場合はメイン UI を即時描画する。
//   2. SDK の `MeidenConnectCredentialsProvider` は `<Suspense fallback={null}>` と
//      `<SilentSubtreeErrorBoundary>` で囲まれた不可視サブツリーにのみマウントする。
//      トークン更新で SDK 内部が Suspense を発火しても、メイン UI が空白にならないようにする。
//   3. SessionSnapshot がローカルからも IndexedDB からも復元できなかった場合のみ、
//      SDK Provider を可視 fallback と一緒にマウントしてサインインへ誘導する。
export function AuthShell({ children }: Props): ReactNode {
  const config = getWebConfig();

  if (config.authMode === "mock") {
    return (
      <MockAuthShell>
        <MockCurrentActorProvider>
          <MockApiRequestProvider>{children}</MockApiRequestProvider>
        </MockCurrentActorProvider>
      </MockAuthShell>
    );
  }

  // 参照専用の埋め込みモード（?embed=1 で /c/:id を開いた場合）。mock 判定の後・required の
  // IdP 経路（コールバック処理・SDK Provider・分離ストレージ読取）へ入る前に分岐し、
  // 親フレームからのトークン受け渡しだけで認証する。IdP へのリダイレクトは一切行わない。
  if (isEmbedMode()) {
    return <EmbedAuthShell>{children}</EmbedAuthShell>;
  }

  if (!config.authServiceOrigin || !config.serviceOrigin) {
    throw new Error(
      "authMode=required では authServiceOrigin と serviceOrigin の設定が必須です。/config/runtime.json を確認してください。"
    );
  }

  const hasAuthorizationCallbackParameters = hasMeidenConnectAuthorizationCallbackParameters();
  if (hasAuthorizationCallbackParameters && isSignedOut()) {
    // 別タブのサインアウトと競合して遅れて到着した認可コールバックは処理しない。
    // 古い code を残すと、明示ログインでフラグを解除した直後に再交換されるため、
    // URL からも除去してサインアウト状態の案内へ進める。
    clearStaleAuthorizationCallbackParameters();
  } else if (hasAuthorizationCallbackParameters) {
    // 同一タブで同じ state の /idp-cb に再着地し、かつ MEIDEN CONNECT 認証済みの
    // SessionSnapshot が既に残っている場合は、URL hash の `code` を SDK 内部が既に
    // 消費済みの可能性が高い (SDK 配下のクレデンシャルプロバイダーが裏で同じ
    // /api/oidc/token を叩いて成功させた後で利用者が手動リロードしたケース等)。
    // そのままペインを起動して `completeAuthorizationCallbackWithoutSdk` がもう一度
    // 同じ code を交換しに行くと、IdP が invalid_grant の連発を rate-limit として
    // 429 で拒否し、「アクセスが集中…」案内へ落ち続ける悪循環になる。この場合は
    // hash を捨ててアプリトップへ戻し、bootstrap 経路で IndexedDB の refresh token
    // から再開させる。
    //
    // 一方、以下の場合は通常通り callback を処理させる。
    // - 初回 /idp-cb 着地 (同 state を同タブで見たことがない) = 新規 code の正当な交換
    // - SessionSnapshot が無い / mock 由来 = 初回サインインフローや別アカウントログイン
    //
    // なお seen state の記録には sessionStorage を使っているため、別タブで先に同じ
    // /idp-cb#... を開いた直後にこのタブで同じ URL を開いた場合は cross-tab 共有が
    // 効かず、callback ペインが再起動する余地が残る。ただし利用者の主訴 (同一タブで
    // 1 時間後にエラー画面が出る) は SDK 内部の自動 refresh→再認可→リロードの流れ
    // なので、同一タブ抑止で実用上の主訴は解消する想定。
    const callbackState = readMeidenConnectCallbackState();
    const snapshot = loadSessionSnapshot();
    const isSameStateReentry =
      callbackState !== undefined && hasSeenMeidenConnectCallbackState(callbackState);
    const hasMeidenConnectSnapshot = snapshot?.source === "meiden-connect";
    if (isSameStateReentry && hasMeidenConnectSnapshot) {
      if (typeof window !== "undefined") {
        window.location.replace(new URL("/", window.location.origin).href);
      }
      return null;
    }
    if (callbackState !== undefined) {
      markMeidenConnectCallbackStateSeen(callbackState);
    }
    return (
      <MeidenConnectAuthorizationCallbackPane
        authServiceOrigin={config.authServiceOrigin}
        className="app-bootstrap"
        serviceOrigin={config.serviceOrigin}
      />
    );
  }

  if (hasPendingMeidenConnectTokenStoreReset()) {
    return (
      <MeidenConnectTokenStoreResetPane
        authServiceOrigin={config.authServiceOrigin}
        className="app-bootstrap"
        serviceOrigin={config.serviceOrigin}
      />
    );
  }

  return (
    <MeidenConnectAuthShell
      authServiceOrigin={config.authServiceOrigin}
      serviceOrigin={config.serviceOrigin}
    >
      <AuthenticatedApiRequestProvider>{children}</AuthenticatedApiRequestProvider>
    </MeidenConnectAuthShell>
  );
}

function MockAuthShell({ children }: { readonly children: ReactNode }): ReactNode {
  const signOut = useCallback(async () => {
    // mock 認証ではログイン状態を固定し、ローカル確認中に未ログイン状態を作らない。
  }, []);
  const value = useMemo<AuthSessionContextValue>(
    () => ({ user: MOCK_SESSION, isRestoring: false, signOut }),
    [signOut]
  );
  return <AuthSessionContext.Provider value={value}>{children}</AuthSessionContext.Provider>;
}

interface MeidenConnectAuthShellProps {
  readonly authServiceOrigin: string;
  readonly serviceOrigin: string;
  readonly children: ReactNode;
}

type SignOutPhase = "none" | "redirecting" | "waiting" | "finishing" | "signed-out" | "failed";

/**
 * MEIDEN CONNECT モード用の認証 Shell 本体。
 *
 * SessionSnapshot のキャッシュ管理と、SDK Provider のサブツリー隔離マウントを担当する。
 */
function MeidenConnectAuthShell({
  authServiceOrigin,
  serviceOrigin,
  children,
}: MeidenConnectAuthShellProps): ReactNode {
  // localStorage の SessionSnapshot で即時起動できるよう、初期値は localStorage から読み出す。
  const [session, setSession] = useState<SessionSnapshot | undefined>(() => loadSessionSnapshot());
  // 起動直後に IndexedDB から復元を試みている間は、未ログインとして扱わないように
  // restoring フラグを立てる。
  const [isBootstrapping, setIsBootstrapping] = useState<boolean>(
    () => loadSessionSnapshot() === undefined
  );
  // bootstrap で MEIDEN CONNECT IdP から 429 を受け取った場合に設定する。
  // この状態では SDK Provider の可視 fallback マウントを抑止し、専用の案内パネルへ
  // 切り替える。SDK Provider をそのままマウントすると IdP の連続失敗カウンタが
  // 更に押し上がり、ブロック時間が延長される恐れがある。
  const [bootstrapRateLimitState, setBootstrapRateLimitState] = useState<
    { readonly retryAfterSeconds: number | undefined } | undefined
  >(undefined);
  // MEIDEN CONNECT token endpoint が rate-limit 中の場合、SDK Provider を再マウントすると
  // 再認可遷移が先に走り、/idp-cb の rate-limit 画面へ戻されることがある。
  // localStorage / BroadcastChannel の共有状態を親 Shell でも監視し、解除までは SDK Provider
  // を止めて SessionSnapshot ベースの画面表示を維持する。
  const [tokenEndpointRateLimitedUntil, setTokenEndpointRateLimitedUntilState] = useState<
    number | undefined
  >(() => getTokenEndpointRateLimitedUntil());
  // 復帰マーカーは初回描画で判定し、SDK Provider を一度もマウントせず後片付けへ入る。
  const [signOutPhase, setSignOutPhase] = useState<SignOutPhase>(readInitialSignOutPhase);
  const [hasResidualTokens, setHasResidualTokens] = useState(false);
  // 復元中に別タブ同期 / signIn 等で session が変化した場合に、古い Snapshot で
  // 上書きしないよう、最新の session 値を ref で参照できるようにする。
  const sessionRef = useRef(session);
  // 別タブログアウト等で SessionSnapshot が `undefined` に戻った場合、`sessionRef.current` だけ
  // では「bootstrap 開始時点と同じ未ログイン状態」と区別できない。世代カウンタを別途持ち、
  // bootstrap 開始時と finally 時点で世代が一致しない場合は復元結果で巻き戻さない。
  const sessionSnapshotRevisionRef = useRef(0);
  useEffect(() => {
    sessionRef.current = session;
  }, [session]);

  // IdP から戻った直後は、URL マーカーを消費してからローカルトークンを再度破棄する。
  // 破棄の完了前に成功表示を出さず、残存があれば利用者へ明示する。
  useEffect(() => {
    if (signOutPhase !== "finishing") {
      return;
    }

    clearSignedOutReturnMarker();
    markSignedOut();
    clearSessionSnapshot();
    setSession(undefined);
    setIsBootstrapping(false);
    void purgeAllMeidenConnectTokens({ authServiceOrigin, serviceOrigin })
      .then((result) => {
        if (result.remainingUserIds.length > 0) {
          setHasResidualTokens(true);
          console.warn(
            "[auth] サインアウト後もローカルトークンが残存しています",
            result.remainingUserIds.length
          );
        }
      })
      .catch(() => setHasResidualTokens(true))
      .finally(() => {
        markSignOutCompleted();
        setSignOutPhase("signed-out");
      });
  }, [signOutPhase, authServiceOrigin, serviceOrigin]);

  // 別タブログインや待機後の復元で session が確立したら、429 案内の固定表示を解除する。
  useEffect(() => {
    if (session !== undefined && bootstrapRateLimitState !== undefined) {
      setBootstrapRateLimitState(undefined);
    }
  }, [session, bootstrapRateLimitState]);

  // bootstrap 経路で設定した 429 案内は、共有 rate-limit 状態の解除後に残さない。
  // 残ったままだと待機後も refresh 再試行へ進まず、案内画面で停止してしまう。
  useEffect(() => {
    if (tokenEndpointRateLimitedUntil === undefined && bootstrapRateLimitState !== undefined) {
      setBootstrapRateLimitState(undefined);
    }
  }, [bootstrapRateLimitState, tokenEndpointRateLimitedUntil]);

  // rate-limit 共有状態は他タブからも更新されるため、BroadcastChannel / storage 由来の通知、
  // タブ復帰、解除予定時刻のタイマーで読み直す。
  useEffect(() => {
    const refreshTokenEndpointRateLimit = (): void => {
      setTokenEndpointRateLimitedUntilState(getTokenEndpointRateLimitedUntil());
    };
    refreshTokenEndpointRateLimit();

    const unsubscribe = subscribeTokenBroadcast((message) => {
      if (
        message.type === "RATE_LIMITED" ||
        message.type === "RATE_LIMIT_CLEARED" ||
        message.type === "REFRESH_DONE"
      ) {
        refreshTokenEndpointRateLimit();
      }
    });

    window.addEventListener("focus", refreshTokenEndpointRateLimit);
    document.addEventListener("visibilitychange", refreshTokenEndpointRateLimit);

    return () => {
      unsubscribe();
      window.removeEventListener("focus", refreshTokenEndpointRateLimit);
      document.removeEventListener("visibilitychange", refreshTokenEndpointRateLimit);
    };
  }, []);

  useEffect(() => {
    if (tokenEndpointRateLimitedUntil === undefined) {
      return undefined;
    }
    const delayMs = Math.max(0, tokenEndpointRateLimitedUntil - Date.now()) + 250;
    const timeoutId = window.setTimeout(
      () => {
        setTokenEndpointRateLimitedUntilState(getTokenEndpointRateLimitedUntil());
      },
      Math.min(delayMs, 2_147_483_647)
    );

    return () => window.clearTimeout(timeoutId);
  }, [tokenEndpointRateLimitedUntil]);

  // 他タブで SessionSnapshot が変化した場合 (signOut / 別端末同期) に追従する。
  useEffect(() => {
    const unsubscribe = subscribeSessionSnapshot(() => {
      sessionSnapshotRevisionRef.current += 1;
      const signedOutState = getSignedOutState();
      if (signedOutState !== "none") {
        // 別タブのサインアウト開始を受け取った時点で SDK Provider を停止し、
        // 残存トークンや IdP セッションから自動再認可されないようにする。
        sessionRef.current = undefined;
        setSession(undefined);
        setIsBootstrapping(false);
        setSignOutPhase((current) => {
          if (current === "redirecting" || current === "finishing") {
            return current;
          }
          if (signedOutState === "completed") {
            return "signed-out";
          }
          return signedOutState === "failed" ? "failed" : "waiting";
        });
        return;
      }
      const latest = loadSessionSnapshot();
      // bootstrap finally 経路が同期的に参照できるよう sessionRef もここで更新する。
      sessionRef.current = latest;
      setSession((current) => (areSnapshotsEqual(current, latest) ? current : latest));
    });
    return unsubscribe;
  }, []);

  // 初回マウント時に SessionSnapshot が localStorage に無ければ、IndexedDB から復元を試みる。
  useEffect(() => {
    if (signOutPhase !== "none") {
      // サインアウト中や完了直後は、残存トークンから SessionSnapshot を復元しない。
      return undefined;
    }
    if (session !== undefined) {
      setIsBootstrapping(false);
      return undefined;
    }
    if (bootstrapRateLimitState !== undefined || tokenEndpointRateLimitedUntil !== undefined) {
      // rate-limit 中は refresh 自体を投げず、解除後に改めて bootstrap へ戻す。
      if (!isBootstrapping) {
        setIsBootstrapping(true);
      }
      return undefined;
    }

    let active = true;
    const startedSessionSnapshotRevision = sessionSnapshotRevisionRef.current;
    void restoreSessionSnapshotFromMeidenConnect({
      authServiceOrigin,
      serviceOrigin,
    })
      .then((result) => {
        if (!active) {
          return;
        }
        if (result.status === "rate-limited") {
          // IdP がレートリミット中。SDK Provider を可視マウントせずに、専用パネルで
          // 利用者へ待機を案内する経路へ切り替える。
          const rateLimitedUntil = rememberTokenEndpointRateLimitedUntil(result.retryAfterSeconds);
          setTokenEndpointRateLimitedUntilState(rateLimitedUntil);
          broadcastTokenMessage({
            type: "RATE_LIMITED",
            until: rateLimitedUntil,
            observedAt: Date.now(),
          });
          setBootstrapRateLimitState({ retryAfterSeconds: result.retryAfterSeconds });
          return;
        }
        if (result.status !== "session") {
          return;
        }
        // 復元中に他経路 (別タブ同期・初回サインイン bridge・別タブ signOut) で session 状態が
        // 変化していた場合は、古い Snapshot で新しい状態を巻き戻さないよう書き込みをスキップする。
        // `sessionRef.current !== undefined` は新規ログイン側、世代不一致は別タブ signOut 側を
        // それぞれ拾う。世代カウンタは `subscribeSessionSnapshot` 内で増えるため、
        // `clearSessionSnapshot` で `latest=undefined` になった場合も検出できる。
        if (
          sessionRef.current !== undefined ||
          sessionSnapshotRevisionRef.current !== startedSessionSnapshotRevision
        ) {
          return;
        }
        saveSessionSnapshot(result.snapshot);
        setSession(result.snapshot);
      })
      .finally(() => {
        if (active) {
          setIsBootstrapping(false);
        }
      });

    return () => {
      active = false;
    };
    // authServiceOrigin / serviceOrigin は親から渡される runtime config で実行中に変わらない。
  }, [
    authServiceOrigin,
    bootstrapRateLimitState,
    isBootstrapping,
    serviceOrigin,
    session,
    signOutPhase,
    tokenEndpointRateLimitedUntil,
  ]);

  // 描画を先に切り替えて SDK Provider をアンマウントし、その後の effect で破棄と遷移を行う。
  const requestSignOut = useCallback(async (): Promise<void> => {
    setSignOutPhase("redirecting");
  }, []);

  useEffect(() => {
    if (signOutPhase !== "redirecting" || !tryBeginSignOut()) {
      return;
    }

    const userId = sessionRef.current?.userId;
    void (async () => {
      // ID トークンの読取待機中にも、別タブの SDK Provider を先に停止する。
      markSignedOut();

      let signOutUrl: string | undefined;
      try {
        const idToken =
          userId === undefined
            ? undefined
            : await readMeidenConnectIdToken({
                authServiceOrigin,
                serviceOrigin,
                userId,
              }).catch(() => undefined);
        signOutUrl = buildMeidenConnectSignOutUrl({
          authServiceOrigin,
          serviceOrigin,
          idToken,
        });
      } catch {
        // URL や id_token_hint を例外出力へ混ぜないため、詳細値はログへ出さない。
        console.error("[auth] サインアウト URL を組み立てられませんでした");
      }

      const purgeResult = await purgeAllMeidenConnectTokens({
        authServiceOrigin,
        serviceOrigin,
      }).catch(() => ({ remainingUserIds: ["unknown"] as readonly string[] }));
      const hasResidual = purgeResult.remainingUserIds.length > 0;
      if (hasResidual) {
        console.warn(
          "[auth] ローカルトークンを破棄しきれませんでした",
          purgeResult.remainingUserIds.length
        );
      }
      clearSessionSnapshot();
      setSession(undefined);

      if (signOutUrl === undefined) {
        markSignOutFailed();
        releaseSignOutAttempt();
        setHasResidualTokens(hasResidual);
        setSignOutPhase("failed");
        return;
      }
      try {
        navigateToSignOut(signOutUrl);
      } catch {
        // 遷移自体に失敗した場合も成功扱いせず、値をログへ出さずに再試行を許可する。
        console.error("[auth] サインアウト先へ遷移できませんでした");
        markSignOutFailed();
        releaseSignOutAttempt();
        setHasResidualTokens(hasResidual);
        setSignOutPhase("failed");
      }
    })();
  }, [signOutPhase, authServiceOrigin, serviceOrigin]);

  const contextValue = useMemo<AuthSessionContextValue>(
    () => ({
      user: session,
      isRestoring: isBootstrapping,
      signOut: requestSignOut,
    }),
    [session, isBootstrapping, requestSignOut]
  );

  if (signOutPhase === "redirecting" || signOutPhase === "finishing") {
    return (
      <AuthSessionContext.Provider value={contextValue}>
        <div className="app-bootstrap" aria-busy="true" aria-live="polite">
          <p>
            {signOutPhase === "redirecting"
              ? "サインアウトしています…"
              : "サインアウトを完了しています…"}
          </p>
        </div>
      </AuthSessionContext.Provider>
    );
  }

  if (signOutPhase === "waiting") {
    return (
      <AuthSessionContext.Provider value={contextValue}>
        <SignOutPendingPane onRetry={() => setSignOutPhase("redirecting")} />
      </AuthSessionContext.Provider>
    );
  }

  if (signOutPhase === "signed-out") {
    return (
      <AuthSessionContext.Provider value={contextValue}>
        <SignedOutPane
          hasResidualTokens={hasResidualTokens}
          onSignIn={() => {
            clearSignedOut();
            setSignOutPhase("none");
          }}
        />
      </AuthSessionContext.Provider>
    );
  }

  if (signOutPhase === "failed") {
    return (
      <AuthSessionContext.Provider value={contextValue}>
        <SignOutFailedPane
          hasResidualTokens={hasResidualTokens}
          onRetry={() => setSignOutPhase("redirecting")}
        />
      </AuthSessionContext.Provider>
    );
  }

  // 既に bootstrap、別タブ、/idp-cb のいずれかで token endpoint の 429 が観測済みの場合は、
  // 可視 SDK Provider を起動せず待機案内に留める。これにより、TOP 復帰直後に再び /idp-cb
  // へ戻るループを防ぐ。
  if (session === undefined && tokenEndpointRateLimitedUntil !== undefined) {
    return (
      <AuthSessionContext.Provider value={contextValue}>
        <BootstrapRateLimitedPane
          retryAfterSeconds={readRemainingRateLimitSeconds(tokenEndpointRateLimitedUntil)}
        />
      </AuthSessionContext.Provider>
    );
  }

  // bootstrap が IdP のレートリミット (HTTP 429) を観測した場合は、SDK Provider の
  // 可視マウントを避けて「待機 + 再読込」だけを案内する。
  if (session === undefined && bootstrapRateLimitState !== undefined) {
    return (
      <AuthSessionContext.Provider value={contextValue}>
        <BootstrapRateLimitedPane retryAfterSeconds={bootstrapRateLimitState.retryAfterSeconds} />
      </AuthSessionContext.Provider>
    );
  }

  // セッション未取得かつ復元処理中: SDK Provider を可視マウントしない。
  // メイン UI 側は AuthSessionContext を介して user=undefined / isRestoring=true を観測できる。
  // 起動直後のごく短い時間に空白フォールバックが出ないよう、目立たないローディング表示にする。
  if (isBootstrapping && session === undefined) {
    return (
      <AuthSessionContext.Provider value={contextValue}>
        <MeidenConnectAuthLoadingPane
          authServiceOrigin={authServiceOrigin}
          className="app-bootstrap"
          serviceOrigin={serviceOrigin}
        />
      </AuthSessionContext.Provider>
    );
  }

  // セッション未取得かつ復元失敗: SDK Provider を可視マウントしてサインインを誘導する。
  // 通常運用では「初回サインイン」「リフレッシュトークン失効」のときだけここに来る。
  if (session === undefined) {
    return (
      <AuthSessionContext.Provider value={contextValue}>
        <Suspense
          fallback={
            <MeidenConnectAuthLoadingPane
              authServiceOrigin={authServiceOrigin}
              className="app-bootstrap"
              serviceOrigin={serviceOrigin}
            />
          }
        >
          <MeidenConnectCredentialsProvider
            authServiceOrigin={authServiceOrigin}
            origin={serviceOrigin}
          >
            <MeidenConnectAuthRecoverySuccessMarker />
            <MeidenConnectInitialSignInBridge
              onSnapshotChange={(next) => {
                if (isSignedOut()) {
                  return;
                }
                saveSessionSnapshot(next);
                setSession(next);
              }}
            />
          </MeidenConnectCredentialsProvider>
        </Suspense>
      </AuthSessionContext.Provider>
    );
  }

  // セッション取得済み: メイン UI は SessionSnapshot キャッシュから即時描画。
  // SDK Provider は不可視サブツリーでプロフィール同期と signOut を担う。
  return (
    <AuthSessionContext.Provider value={contextValue}>
      {children}
      {session.source === "meiden-connect" && tokenEndpointRateLimitedUntil === undefined ? (
        <SilentSubtreeErrorBoundary>
          <Suspense fallback={null}>
            <MeidenConnectCredentialsProvider
              authServiceOrigin={authServiceOrigin}
              origin={serviceOrigin}
            >
              <MeidenConnectAuthRecoverySuccessMarker />
              <MeidenConnectSessionSyncSubtree
                currentSession={session}
                onSnapshotChange={(next) => {
                  if (isSignedOut()) {
                    return;
                  }
                  saveSessionSnapshot(next);
                  setSession(next);
                }}
              />
            </MeidenConnectCredentialsProvider>
          </Suspense>
        </SilentSubtreeErrorBoundary>
      ) : null}
    </AuthSessionContext.Provider>
  );
}

function readInitialSignOutPhase(): SignOutPhase {
  if (hasSignedOutReturnMarker()) {
    return "finishing";
  }
  const signedOutState = getSignedOutState();
  if (signedOutState === "completed") {
    return "signed-out";
  }
  if (signedOutState === "failed") {
    return "failed";
  }
  return signedOutState === "pending" ? "waiting" : "none";
}

/** サインアウト開始後に遅れて到着した認可コールバックを同一オリジン内で破棄する。 */
function clearStaleAuthorizationCallbackParameters(): void {
  if (typeof window === "undefined") {
    return;
  }
  try {
    window.history.replaceState(window.history.state, "", new URL("/", window.location.origin));
  } catch {
    // URL を消せない環境でも認可コールバック自体は処理せず、永続状態で復元を拒否する。
  }
}

function readRemainingRateLimitSeconds(rateLimitedUntil: number): number {
  return Math.max(1, Math.ceil((rateLimitedUntil - Date.now()) / 1_000));
}

interface MeidenConnectSessionSyncSubtreeProps {
  readonly currentSession: SessionSnapshot;
  readonly onSnapshotChange: (snapshot: SessionSnapshot) => void;
}

/**
 * SDK Provider 配下で動作する「プロフィール同期 + signOut」処理本体。
 *
 * 自身は何も描画せず、SDK から得られた情報を親の AuthShell にコールバックで返す。
 * Suspense fallback=null + SilentSubtreeErrorBoundary 配下で動かすため、本サブツリーが
 * SDK 初期化で Suspense したり例外を投げたりしても、メイン UI には影響しない。
 */
function MeidenConnectSessionSyncSubtree({
  currentSession,
  onSnapshotChange,
}: MeidenConnectSessionSyncSubtreeProps): ReactNode {
  const user = useMeidenConnectUser();
  const profileResult = useMeidenConnectIdpTableGetUser(user.userId);
  const avatarImageUrl = resolveSessionAvatarImageUrl({
    hasProfileRecord: profileResult.data !== undefined,
    profileAvatarImageUrl: profileResult.data?.avatarImageUrl,
    tokenAvatarImageUrl: user.avatarImageUrl,
  });
  const lastReportedSnapshotRef = useRef<SessionSnapshot | undefined>(currentSession);
  const snapshot = useMemo<SessionSnapshot>(
    () => ({
      userId: user.userId,
      name: user.name,
      email: user.email,
      affiliation: user.department,
      avatarImageUrl,
      source: "meiden-connect",
      globalPermissionIds: [...user.globalPermissionIds],
    }),
    [avatarImageUrl, user.department, user.email, user.globalPermissionIds, user.name, user.userId]
  );

  // SDK から得た最新の snapshot を親へ伝える。変化がなければ通知しない。
  useEffect(() => {
    if (areSnapshotsEqual(lastReportedSnapshotRef.current, snapshot)) {
      return;
    }
    lastReportedSnapshotRef.current = snapshot;
    onSnapshotChange(snapshot);
  }, [snapshot, onSnapshotChange]);

  return null;
}

interface MeidenConnectInitialSignInBridgeProps {
  readonly onSnapshotChange: (snapshot: SessionSnapshot) => void;
}

/**
 * 初回サインイン経路。SessionSnapshot が一切無いときだけマウントされる。
 *
 * SDK Provider 配下で `useMeidenConnectUser` を読み、認可フローが完了して有効な
 * トークンが取れた時点で snapshot を親へ伝え、メイン UI を起動させる。
 */
function MeidenConnectInitialSignInBridge({
  onSnapshotChange,
}: MeidenConnectInitialSignInBridgeProps): ReactNode {
  const user = useMeidenConnectUser();
  const profileResult = useMeidenConnectIdpTableGetUser(user.userId);
  const avatarImageUrl = resolveSessionAvatarImageUrl({
    hasProfileRecord: profileResult.data !== undefined,
    profileAvatarImageUrl: profileResult.data?.avatarImageUrl,
    tokenAvatarImageUrl: user.avatarImageUrl,
  });
  const lastReportedSnapshotRef = useRef<SessionSnapshot | undefined>(undefined);

  const snapshot = useMemo<SessionSnapshot>(
    () => ({
      userId: user.userId,
      name: user.name,
      email: user.email,
      affiliation: user.department,
      // 初回サインインでは idp-file: 解決完了を待たず、ID トークンの画像 URL をひとまず採用する。
      // サブツリー側の usePortableAvatarImageUrl は session 取得後にマウントされて再度走る。
      avatarImageUrl: isIdpFileUrl(avatarImageUrl) ? undefined : avatarImageUrl,
      source: "meiden-connect",
      globalPermissionIds: [...user.globalPermissionIds],
    }),
    [avatarImageUrl, user.department, user.email, user.globalPermissionIds, user.name, user.userId]
  );

  useEffect(() => {
    if (areSnapshotsEqual(lastReportedSnapshotRef.current, snapshot)) {
      return;
    }
    lastReportedSnapshotRef.current = snapshot;
    onSnapshotChange(snapshot);
  }, [snapshot, onSnapshotChange]);

  return null;
}

function isIdpFileUrl(value: string | undefined): value is string {
  return value?.startsWith("idp-file:") === true;
}

/** グローバルサインアウト完了後の案内。 */
function SignedOutPane({
  hasResidualTokens,
  onSignIn,
}: {
  readonly hasResidualTokens: boolean;
  readonly onSignIn: () => void;
}): ReactNode {
  return (
    <div className="app-bootstrap" role="status" aria-live="polite">
      <div>
        <p>サインアウトしました。</p>
        {hasResidualTokens ? (
          <p>
            この端末に保存されたトークンの一部を削除できませんでした。
            共用端末をお使いの場合は、ブラウザのサイトデータを消去してください。
          </p>
        ) : (
          <p>ご利用ありがとうございました。</p>
        )}
        <p>
          <button type="button" onClick={onSignIn}>
            ログインする
          </button>
        </p>
      </div>
    </div>
  );
}

/** 別タブまたは再読込で観測したサインアウト処理中の案内。 */
function SignOutPendingPane({ onRetry }: { readonly onRetry: () => void }): ReactNode {
  return (
    <div className="app-bootstrap" role="status" aria-live="polite">
      <div>
        <p>サインアウト処理の完了を待っています。</p>
        <p>完了画面へ戻らない場合は、もう一度サインアウトしてください。</p>
        <p>
          <button type="button" onClick={onRetry}>
            もう一度サインアウトする
          </button>
        </p>
      </div>
    </div>
  );
}

/** IdP のサインアウト URL を構築できなかった場合の案内。 */
function SignOutFailedPane({
  hasResidualTokens,
  onRetry,
}: {
  readonly hasResidualTokens: boolean;
  readonly onRetry: () => void;
}): ReactNode {
  return (
    <div className="app-bootstrap" role="alert" aria-live="assertive">
      <div>
        <p>サインアウトを完了できませんでした。</p>
        <p>MEIDEN CONNECT 側のセッションは解除されていません。</p>
        {hasResidualTokens ? (
          <p>
            この端末に保存されたトークンの一部も削除できませんでした。
            共用端末をお使いの場合は、ブラウザのサイトデータを消去してください。
          </p>
        ) : (
          <p>この端末に保存されたトークンは削除しました。</p>
        )}
        <p>設定（認証サービスの URL）に問題がある可能性があります。管理者へご連絡ください。</p>
        <p>
          <button type="button" onClick={onRetry}>
            もう一度サインアウトする
          </button>
        </p>
      </div>
    </div>
  );
}

/**
 * bootstrap の refresh で MEIDEN CONNECT IdP から 429 を受け取った際の案内パネル。
 *
 * SDK Provider の可視 fallback を起動すると IdP 側の連続失敗カウンタが更に積み上がり
 * ブロック時間が延長されるため、ここでは「待機 + 再読込」のみを促す。
 */
function BootstrapRateLimitedPane({
  retryAfterSeconds,
}: {
  readonly retryAfterSeconds: number | undefined;
}): ReactNode {
  const handleReload = useCallback((): void => {
    window.location.reload();
  }, []);

  const waitGuidance = formatRateLimitWaitGuidance(retryAfterSeconds);

  return (
    <div className="app-bootstrap" role="alert" aria-live="polite">
      <div>
        <p>
          MEIDEN CONNECT
          認証サーバーへのアクセスが集中しているため、認証応答を受け取れませんでした。
        </p>
        <p>{waitGuidance}</p>
        <p>業務データは削除されません。サインイン状態も維持されています。</p>
        <p>
          <button type="button" onClick={handleReload}>
            再読込する
          </button>
        </p>
      </div>
    </div>
  );
}

function formatRateLimitWaitGuidance(retryAfterSeconds: number | undefined): string {
  if (retryAfterSeconds === undefined) {
    return "しばらく時間を置いてから、もう一度アクセスしてください。";
  }
  if (retryAfterSeconds >= 60) {
    const minutes = Math.max(1, Math.ceil(retryAfterSeconds / 60));
    return `${minutes} 分ほど時間を置いてから、もう一度アクセスしてください。`;
  }
  return `${retryAfterSeconds} 秒ほど時間を置いてから、もう一度アクセスしてください。`;
}

export function resolveSessionAvatarImageUrlForTest(input: {
  readonly hasProfileRecord: boolean;
  readonly profileAvatarImageUrl: string | null | undefined;
  readonly tokenAvatarImageUrl: string | null | undefined;
}): string | undefined {
  return resolveSessionAvatarImageUrl(input);
}

function resolveSessionAvatarImageUrl(input: {
  readonly hasProfileRecord: boolean;
  readonly profileAvatarImageUrl: string | null | undefined;
  readonly tokenAvatarImageUrl: string | null | undefined;
}): string | undefined {
  // 認証復旧直後は ID トークン側の iconUrl が空になることがあるため、
  // IdP の User テーブルに最新プロフィールが取れている場合はそちらを正本にする。
  if (input.hasProfileRecord) {
    return normalizeAvatarImageUrl(input.profileAvatarImageUrl);
  }

  return normalizeAvatarImageUrl(input.tokenAvatarImageUrl);
}

function normalizeAvatarImageUrl(value: string | null | undefined): string | undefined {
  if (typeof value !== "string") {
    return undefined;
  }

  const trimmedValue = value.trim();
  return trimmedValue.length > 0 ? trimmedValue : undefined;
}

function areSnapshotsEqual(
  a: SessionSnapshot | undefined,
  b: SessionSnapshot | undefined
): boolean {
  if (a === b) {
    return true;
  }
  if (a === undefined || b === undefined) {
    return false;
  }
  return (
    a.userId === b.userId &&
    a.name === b.name &&
    a.email === b.email &&
    a.affiliation === b.affiliation &&
    a.avatarImageUrl === b.avatarImageUrl &&
    a.source === b.source &&
    areStringArraysEqual(a.globalPermissionIds, b.globalPermissionIds)
  );
}

function areStringArraysEqual(
  a: readonly string[] | undefined,
  b: readonly string[] | undefined
): boolean {
  if (a === b) {
    return true;
  }
  if (a === undefined || b === undefined) {
    return a === undefined && b === undefined;
  }
  if (a.length !== b.length) {
    return false;
  }
  const sortedA = [...a].sort();
  const sortedB = [...b].sort();
  return sortedA.every((value, index) => value === sortedB[index]);
}

/**
 * SDK 同期サブツリー専用のエラーバウンダリー。
 *
 * SDK 初期化や IndexedDB アクセスで投げられた例外がメイン UI に伝播しないよう静かに握りつぶす。
 * サブツリーが失敗してもキャッシュ済みの SessionSnapshot で UI を続行できる。
 * 一度エラー状態になっても、その後のタブ復帰 (visibility=visible) / focus / online
 * イベントで内部状態をリセットし、サブツリーを再マウントして同期を再開できるようにする。
 */
class SilentSubtreeErrorBoundary extends Component<
  { readonly children: ReactNode },
  { readonly hasError: boolean }
> {
  public override state: { readonly hasError: boolean } = { hasError: false };

  public static getDerivedStateFromError(): { readonly hasError: boolean } {
    return { hasError: true };
  }

  public override componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.warn(
      "[auth] バックグラウンド認証同期サブツリーで例外が発生したためサイレントに停止しました",
      error,
      errorInfo
    );
  }

  public override componentDidMount(): void {
    if (typeof document === "undefined" || typeof window === "undefined") {
      return;
    }
    window.addEventListener("focus", this.handleRecoveryTrigger);
    window.addEventListener("online", this.handleRecoveryTrigger);
    document.addEventListener("visibilitychange", this.handleVisibilityChange);
  }

  public override componentWillUnmount(): void {
    if (typeof document === "undefined" || typeof window === "undefined") {
      return;
    }
    window.removeEventListener("focus", this.handleRecoveryTrigger);
    window.removeEventListener("online", this.handleRecoveryTrigger);
    document.removeEventListener("visibilitychange", this.handleVisibilityChange);
  }

  /** エラー状態のときだけサブツリーを再マウントしてリトライする。健全時は no-op。 */
  private readonly handleRecoveryTrigger = (): void => {
    if (this.state.hasError) {
      this.setState({ hasError: false });
    }
  };

  private readonly handleVisibilityChange = (): void => {
    if (typeof document !== "undefined" && document.visibilityState === "visible") {
      this.handleRecoveryTrigger();
    }
  };

  public override render(): ReactNode {
    return this.state.hasError ? null : this.props.children;
  }
}

// MEIDEN CONNECT 認証コールバックの `state` パラメーターを sessionStorage 上で
// 「処理着手済み」マークするためのキー接頭辞。同タブで同じ state を二度見たかどうかを
// 判定する用途で、cross-tab で共有する必要はないため localStorage ではなく
// sessionStorage を使う。
const MEIDEN_CONNECT_CALLBACK_SEEN_STATE_KEY_PREFIX = "meiden-connect.auth.callback.seen-state.";

/** 現在の URL hash から MEIDEN CONNECT 認証コールバックの `state` を取り出す。 */
function readMeidenConnectCallbackState(): string | undefined {
  if (typeof window === "undefined") {
    return undefined;
  }
  return new URLSearchParams(window.location.hash.slice(1)).get("state") ?? undefined;
}

/** sessionStorage に対象 `state` の「処理着手済み」マークが立っているかを返す。 */
function hasSeenMeidenConnectCallbackState(state: string): boolean {
  if (typeof window === "undefined") {
    return false;
  }
  try {
    return (
      window.sessionStorage.getItem(MEIDEN_CONNECT_CALLBACK_SEEN_STATE_KEY_PREFIX + state) === "1"
    );
  } catch {
    return false;
  }
}

/** sessionStorage に対象 `state` の「処理着手済み」マークを立てる。 */
function markMeidenConnectCallbackStateSeen(state: string): void {
  if (typeof window === "undefined") {
    return;
  }
  try {
    window.sessionStorage.setItem(MEIDEN_CONNECT_CALLBACK_SEEN_STATE_KEY_PREFIX + state, "1");
  } catch {
    // sessionStorage が使えない環境では「同 state 再着地のスキップ」が効かなくなるが、
    // 元の問題 (IdP 429) は最終的に IdP 側の rate-limit cooldown で解消するため
    // 致命的ではない。例外を握りつぶしてフォールバック挙動 (= callback ペイン起動)
    // に任せる。
  }
}
